import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Target, TrendingDown, Users, Shield, Banknote, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { apiPost } from "@/lib/api";

function SkeletonAction() {
  return (
    <div className="glass-card p-6 space-y-4 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/5" />
        <div className="h-5 w-2/3 rounded bg-white/5" />
      </div>
      <div className="space-y-2">
        <div className="h-3.5 w-full rounded bg-white/[0.03]" />
        <div className="h-3.5 w-5/6 rounded bg-white/[0.03]" />
      </div>
      <div className="space-y-3">
        <div className="flex items-start gap-2.5">
          <div className="w-4 h-4 rounded bg-primary/5 shrink-0 mt-0.5" />
          <div className="flex-1 space-y-1.5">
            <div className="h-2.5 w-20 rounded bg-white/[0.03]" />
            <div className="h-3 w-full rounded bg-white/[0.03]" />
          </div>
        </div>
        <div className="flex items-start gap-2.5">
          <div className="w-4 h-4 rounded bg-emerald-400/5 shrink-0 mt-0.5" />
          <div className="flex-1 space-y-1.5">
            <div className="h-2.5 w-24 rounded bg-white/[0.03]" />
            <div className="h-3 w-5/6 rounded bg-white/[0.03]" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function PriorityActions({ companyId }) {
  const [priorities, setPriorities] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const load = async () => {
      if (!companyId) return;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const data = await apiPost("/api/priorities/generate", { company_id: companyId });
          if (!mountedRef.current) return;
          setPriorities(data);
          setLoading(false);
          return;
        } catch (err) {
          if (!mountedRef.current) return;
          if (attempt < 2) await new Promise(r => setTimeout(r, 1200 * (attempt + 1)));
          else toast.error("Could not load priority actions.");
        }
      }
      if (mountedRef.current) setLoading(false);
    };
    load();
    return () => { mountedRef.current = false; };
  }, [companyId]);

  if (loading) {
    return (
      <div className="space-y-4" data-testid="priority-loading">
        <div className="flex items-center gap-3">
          <div className="w-1 h-8 rounded-full bg-primary" />
          <h3 className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Priority Actions</h3>
        </div>
        <div className="rounded-xl border border-primary/15 p-6 flex flex-col items-center gap-4" style={{ background: "linear-gradient(135deg, rgba(74,222,128,0.03) 0%, rgba(2,4,2,0.95) 100%)" }}>
          <div className="w-10 h-10 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <div className="text-center">
            <p className="text-white font-medium mb-1">Generating your action plan</p>
            <p className="text-xs text-white/40">Prioritizing the most impactful steps for your business</p>
          </div>
          <div className="w-full max-w-xs h-1.5 rounded-full bg-white/5 overflow-hidden">
            <div className="h-full rounded-full bg-primary/50 animate-[indeterminate_1.5s_ease-in-out_infinite]" style={{ width: '40%' }} />
          </div>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-5 h-5 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-sm text-white/40">Analyzing priorities based on your sustainability profile</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <SkeletonAction />
          <SkeletonAction />
        </div>
      </div>
    );
  }

  if (!priorities || !priorities.actions?.length) return null;

  return (
    <div className="space-y-4" data-testid="priority-actions">
      <div className="flex items-center gap-3">
        <div className="w-1 h-8 rounded-full bg-primary" />
        <div>
          <h3 className="text-lg font-bold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Priority Actions</h3>
          <p className="text-sm text-white/40">Your clear starting points</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {priorities.actions.map((action, i) => (
          <motion.div
            key={`action-${action.title?.slice(0,20)}-${i}`}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.15 }}
            className="glass-card glass-card-selected p-6 space-y-4"
            data-testid={`priority-action-${i}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
                <Target className="w-5 h-5 text-primary" />
              </div>
              <h4 className="text-lg text-white font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>{action.title}</h4>
            </div>

            <p className="text-white/70 leading-relaxed">{action.action}</p>

            <div className="space-y-2.5">
              <div className="flex items-start gap-2.5">
                <TrendingDown className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Why it matters</span>
                  <p className="text-sm text-white/60">{action.why_it_matters}</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Banknote className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Business benefit</span>
                  <p className="text-sm text-white/60">{action.business_benefit}</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Users className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Market benefit</span>
                  <p className="text-sm text-white/60">{action.market_benefit}</p>
                </div>
              </div>

              <div className="flex items-start gap-2.5">
                <Shield className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">Future readiness</span>
                  <p className="text-sm text-white/60">{action.future_readiness}</p>
                </div>
              </div>
            </div>

            {action.funding_connection && (
              <div className="urgency-banner p-3 flex items-start gap-2">
                <Banknote className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                <p className="text-xs text-white/60">{action.funding_connection}</p>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {priorities.final_message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="glass-card p-6 text-center"
          data-testid="priority-final-message"
        >
          <p className="text-white/70 leading-relaxed">{priorities.final_message}</p>
          <p className="text-primary text-sm mt-2 font-medium">Refine these insights and unlock full recommendations</p>
        </motion.div>
      )}
    </div>
  );
}
